import { CoreText, useTheme, CoreBox } from '@vitvio/design-system';
import { isSameDayInTimezone, toZonedTime, dayjs, useAppInForeground } from '@vitvio/helpers';
import { useMemo, useRef, useEffect, useLayoutEffect, useCallback, useState } from 'react';
import { TimeIndicatorLine } from './TimeIndicatorLine';
import { useMissionControlCalendarContext } from './context/MissionControlCalendarContext';
import { useTimeProviderContext } from '@vitvio/time-components';
import type { SurgeryTimelineFragment$key } from './graphql/__generated__/SurgeryTimelineFragment.graphql';
import { useSurgeryTimeLineData } from './hooks';
import { ORColumn } from './ORColumn';
import { useUserContext } from '@vitvio/user-context';
import { ConfirmPatientModalProvider } from './context/ConfirmPatientModalContext';

const HOURS_IN_DAY = 24;
const MINUTES_IN_HOUR = 60;

const TICKS_IN_HOUR = 4;

interface SurgeryTimelineProps {
  hospitalData: SurgeryTimelineFragment$key;
}

export function SurgeryTimeline({ hospitalData }: SurgeryTimelineProps) {
  const { hospitalTimezone } = useUserContext();
  const { selectedDate } = useMissionControlCalendarContext();
  const { currentTimeInTimezone } = useTimeProviderContext();
  const isInForeground = useAppInForeground();

  const { getLayout } = useTheme();

  const data = useSurgeryTimeLineData(hospitalData);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headerPortalRef = useRef<HTMLDivElement>(null);
  const [headerPortalReady, setHeaderPortalReady] = useState(false);

  useLayoutEffect(() => {
    if (headerPortalRef.current) {
      setHeaderPortalReady(true);
    }
  }, []);

  const minuteHeight = getLayout('timeline', 'minuteHeight', false);
  const headerHeight = getLayout('timeline', 'headerHeight', false);
  const timestampsColumnWidth = getLayout('timeline', 'timestampsColumnWidth', false);

  const minutesInGrid = MINUTES_IN_HOUR / TICKS_IN_HOUR;
  const gridItemHeight = minuteHeight * minutesInGrid;
  const timelineHeight = minuteHeight * MINUTES_IN_HOUR * HOURS_IN_DAY;

  const gridMinutes = useMemo(
    () => Array.from({ length: TICKS_IN_HOUR }).map((_, i) => i * minutesInGrid),
    [minutesInGrid],
  );

  const hourMarkers = useMemo(() => {
    const gridHours = Array.from({ length: HOURS_IN_DAY }).map((_, h) => h);

    const markers = gridHours.flatMap((h) => gridMinutes.map((m) => dayjs().hour(h).minute(m)));

    return markers.map((timestamp) => (
      <CoreBox key={timestamp.toString()} h={gridItemHeight} fullW borderTop="border.subtle">
        <CoreBox p="1">
          <CoreText font="XS_Medium" color="fg.muted">
            {timestamp.format('HH:mm')}
          </CoreText>
        </CoreBox>
      </CoreBox>
    ));
  }, [gridItemHeight, gridMinutes]);

  const isToday = selectedDate && isSameDayInTimezone(selectedDate, currentTimeInTimezone, hospitalTimezone);

  const scrollToCurrentTime = useCallback(
    (instant = true) => {
      if (!scrollContainerRef.current || !isToday) return;

      const now = toZonedTime(dayjs(), hospitalTimezone);
      if (!now) return;

      const hours = now.hour();
      const minutes = now.minute();
      const elapsedMinutes = hours * 60 + minutes;

      const viewportHeight = scrollContainerRef.current.clientHeight;
      const currentTimePosition = minuteHeight * elapsedMinutes;
      const scrollPosition = currentTimePosition - viewportHeight / 2;

      scrollContainerRef.current.scrollTo({
        top: Math.max(0, scrollPosition),
        behavior: instant ? 'instant' : 'smooth',
      });
    },
    [isToday, hospitalTimezone, minuteHeight],
  );

  useEffect(() => {
    if (isInForeground) {
      scrollToCurrentTime();
    }
  }, [isInForeground, scrollToCurrentTime]);

  return (
    <ConfirmPatientModalProvider>
      <CoreBox relative borderRadius="lg" overflowHidden fullH flexColumn>
        <CoreBox bg="bg" borderBottom="border" h={headerHeight} flexRow py="3.5">
          <CoreBox w={timestampsColumnWidth} />
          <CoreBox ref={headerPortalRef} flexRow fullW />
        </CoreBox>
        <CoreBox ref={scrollContainerRef} scrollY flexGrow={1} minH={0}>
          <CoreBox flexRow relative h={timelineHeight} maxH={timelineHeight}>
            <CoreBox absoluteFill>{hourMarkers}</CoreBox>
            <CoreBox flexRow fullW pl={timestampsColumnWidth}>
              {data.operatingRooms?.edges?.map((edge) => {
                if (edge?.node === null) return null;
                return (
                  <ORColumn
                    key={edge?.node?.id}
                    id={edge?.node?.id || ''}
                    headerPortalRef={headerPortalRef}
                    headerPortalReady={headerPortalReady}
                  />
                );
              })}
            </CoreBox>
            {isToday && isInForeground && <TimeIndicatorLine />}
          </CoreBox>
        </CoreBox>
      </CoreBox>
    </ConfirmPatientModalProvider>
  );
}
